import { Rng } from '../core/rng';
import type { VehicleSpec } from './types';

/**
 * Per-car presentation traits, seeded from the vehicle id so every client draws the same car the
 * same way (and so a car keeps its look across respawns of its view). Visual only: nothing here is
 * read by the simulation.
 */
export interface CarTraits {
  /** 0..1 how dirty the car is (mud splash height, road film). */
  grime: number;
  /** 0..1 how old/tired the car is (rust blooms, faded paint, smoky exhaust). */
  age: number;
  /** Vinyl roof colour, or null. */
  vinyl: number | null;
  /** Contrast-painted roof colour (Minnow), or null. */
  roof: number | null;
  /** Roof rack (Minnow, Regent) or ladder rack (Haulmaster). */
  rack: boolean;
  /** Bonnet-to-boot racing stripes colour (Vesper GT), or null. */
  stripes: number | null;
  /** Trade-livery waist band colour (Haulmaster), or null. */
  band: number | null;
  /** Secondary paint for the tipper bed (Bedwell). */
  bed: number;
  /** Upholstery colour. */
  trim: number;
  /** Fictional period number plate, e.g. "KDX 418H". */
  plate: string;
  /** Which end of a damaged bumper lets go first (+1 = left, -1 = right). */
  hangSide: 1 | -1;
  /** Driver's coat colour and skin tone. */
  driverCoat: number;
  driverSkin: number;
  /** Metallic paint (flake sparkle, more metal in the base coat) rather than solid. */
  metallic: boolean;
}

// The light vinyl is parchment, not white: a white roof on a dark car read as a paper patch from above.
const VINYL = [0x151311, 0x2b1d14, 0xbfb49c];
const ROOFS = [0xe9e4d6, 0x16161a];
// No police blue here: a white Vesper with blue stripes read as a patrol car from the high camera.
const STRIPES = [0xf0ece0, 0x141414, 0xc9a227];
const BANDS = [0xe8e4d6, 0x9b2d20, 0x1f3a5f, 0xc9a227, 0x2f4f3a];
const BEDS = [0x6b6862, 0x8a3b22, 0x50555a, 0x6b5a3a];
const TRIMS = [0x2a211b, 0x5a3a24, 0x7a2a1f, 0x3a3a3c, 0x6e5a40];
const COATS = [0x2b2b30, 0x4a3b2a, 0x3b4a5a, 0x5a2a20, 0x2f3a2a, 0x6b6050];
const SKINS = [0xe0b394, 0xc89574, 0x8d5a3c, 0xf0c8a8];
// Letters that were never issued on British plates of the period (I, Q, Z) are left out.
const LETTERS = 'ABCDEFGHJKLMNOPRSTUVWXY';
const SUFFIX = 'FGHJ';

export function carTraits(id: number, spec: VehicleSpec): CarTraits {
  const rng = new Rng((id * 2654435761) >>> 0);
  const pick = <T>(a: readonly T[]): T => a[Math.floor(rng.next() * a.length)]!;
  const grime = rng.next() < 0.2 ? 0.1 : 0.25 + rng.next() * 0.75;
  const age = rng.next();
  const body = spec.body;
  const police = spec.class === 'police';
  const r1 = rng.next();
  const r2 = rng.next();
  const r3 = rng.next();
  const plate =
    pick([...LETTERS]) + pick([...LETTERS]) + pick([...LETTERS]) + ' ' + String(100 + Math.floor(rng.next() * 900)) + pick([...SUFFIX]);
  return {
    grime: police ? grime * 0.4 : grime,
    age: police ? age * 0.3 : age,
    vinyl: (body === 'saloon' && !police && r1 < 0.4) || (body === 'exec' && r1 < 0.5) ? pick(VINYL) : null,
    roof: (body === 'hatch' && r1 < 0.5) || (body === 'estate' && r1 < 0.25) ? pick(ROOFS) : null,
    rack: (body === 'hatch' && r2 < 0.25) || (body === 'van' && r2 < 0.45) || (body === 'saloon' && !police && r2 < 0.1),
    stripes: body === 'coupe' && r1 < 0.4 ? pick(STRIPES) : null,
    band: body === 'van' && r3 < 0.55 ? pick(BANDS) : null,
    bed: pick(BEDS),
    trim: police ? 0x1c1c1e : body === 'cab' ? 0x2a2a2c : pick(TRIMS),
    plate,
    hangSide: rng.next() < 0.5 ? 1 : -1,
    driverCoat: police ? 0x14182a : pick(COATS),
    driverSkin: pick(SKINS),
    // Drawn last so the earlier traits keep their seeds from pass 2.
    metallic: !police && body !== 'cab' && body !== 'lorry' && rng.next() < (body === 'coupe' || body === 'roadster' ? 0.7 : body === 'exec' ? 0.6 : body === 'van' ? 0.2 : 0.45),
  };
}

/**
 * Per-car look overrides: Halloran's workshop (src/activities) puts one on `vehicle.data.look` and bumps
 * `vehicle.data.lookVersion`; the view rebuilds that car's rig. Visual only, like the traits themselves.
 * `trim` lands on whatever the body carries: stripes (Vesper), vinyl roof (Regent), contrast roof
 * (Minnow) or the trade band (Haulmaster).
 */
export interface CarLook {
  metallic?: boolean;
  /** Flat matt paint (Pike's spray bay, src/garage): rough, no clearcoat. Wins over `metallic`. */
  matt?: boolean;
  /** A `WheelStyle` id from meshes.ts. */
  wheels?: string;
  /** Rim colour (hex) over the wheel's painted parts (Pike's spray bay). */
  rim?: number;
  /** Underglow colour (hex): a soft neon pool under the car (Pike's spray bay). */
  neon?: number;
  /** Ride height: -1 lowered, +1 raised (visual only). */
  ride?: number;
  /** Camber step 1..3 (shared/garage.ts CAMBERS) and track step -1..2 (TRACKS): how the wheels sit. */
  camber?: number;
  track?: number;
  trim?: number | null;
  /** The gang livery (WP-X4): this colour on the trim, over the car's own (stripes, vinyl roof, roof, band). */
  livery?: number;
  plate?: string;
}

export const CAR_LOOK_KEY = 'look';
export const CAR_LOOK_VERSION_KEY = 'lookVersion';

/** The traits with a workshop look laid over them. */
export function withLook(t: CarTraits, spec: VehicleSpec, look: CarLook | undefined): CarTraits {
  if (!look) return t;
  const out = { ...t };
  if (look.metallic !== undefined) out.metallic = look.metallic;
  if (look.plate) out.plate = look.plate.toUpperCase().slice(0, 8);
  const trim = look.livery ?? look.trim;
  if (trim !== undefined) {
    if (spec.body === 'coupe') out.stripes = trim;
    else if ((spec.body === 'saloon' && spec.class !== 'police') || spec.body === 'exec') out.vinyl = trim;
    else if (spec.body === 'hatch' || spec.body === 'estate') out.roof = trim;
    else if (spec.body === 'van') out.band = trim;
  }
  return out;
}

/** 3×5 pixel font for plates and roof signs: rows top→bottom, 3 bits each (MSB = left). */
const FONT: Record<string, number[]> = {
  A: [2, 5, 7, 5, 5], B: [6, 5, 6, 5, 6], C: [3, 4, 4, 4, 3], D: [6, 5, 5, 5, 6], E: [7, 4, 6, 4, 7], F: [7, 4, 6, 4, 4],
  G: [3, 4, 5, 5, 3], H: [5, 5, 7, 5, 5], I: [7, 2, 2, 2, 7], J: [1, 1, 1, 5, 2], K: [5, 5, 6, 5, 5], L: [4, 4, 4, 4, 7],
  M: [5, 7, 7, 5, 5], N: [6, 5, 5, 5, 5], O: [2, 5, 5, 5, 2], P: [6, 5, 6, 4, 4], Q: [2, 5, 5, 6, 3], R: [6, 5, 6, 5, 5],
  S: [3, 4, 2, 1, 6], T: [7, 2, 2, 2, 2], U: [5, 5, 5, 5, 7], V: [5, 5, 5, 5, 2], W: [5, 5, 7, 7, 5], X: [5, 5, 2, 5, 5],
  Y: [5, 5, 2, 2, 2], Z: [7, 1, 2, 4, 7],
  '0': [7, 5, 5, 5, 7], '1': [2, 6, 2, 2, 7], '2': [6, 1, 2, 4, 7], '3': [6, 1, 2, 1, 6], '4': [5, 5, 7, 1, 1],
  '5': [7, 4, 6, 1, 6], '6': [3, 4, 7, 5, 7], '7': [7, 1, 2, 2, 2], '8': [7, 5, 7, 5, 7], '9': [7, 5, 7, 1, 6],
  ' ': [0, 0, 0, 0, 0],
};

/**
 * Pack `text` (≤ 8 chars, centred) into 8 glyph codes for the car shader. Bit (row*3 + col) is set
 * when that pixel is lit, with row 0 at the BOTTOM and col 0 at the left.
 */
/** Stripe colour that actually shows on this paint (light stripes on dark cars and vice versa). */
export function stripeFor(stripes: number, paint: number): number {
  const lum = (h: number) => (0.2126 * ((h >> 16) & 255) + 0.7152 * ((h >> 8) & 255) + 0.0722 * (h & 255)) / 255;
  const lp = lum(paint);
  if (Math.abs(lum(stripes) - lp) >= 0.28) return stripes;
  return lp > 0.5 ? 0x141414 : 0xf0ece0;
}

export function glyphCodes(text: string, out: number[] = new Array<number>(8).fill(0)): number[] {
  const t = text.toUpperCase().slice(0, 8);
  const pad = Math.floor((8 - t.length) / 2);
  for (let i = 0; i < 8; i++) {
    const ch = t[i - pad] ?? ' ';
    const rows = FONT[ch] ?? FONT[' ']!;
    let code = 0;
    for (let r = 0; r < 5; r++) {
      const bits = rows[4 - r]!;
      for (let c = 0; c < 3; c++) if (bits & (4 >> c)) code |= 1 << (r * 3 + c);
    }
    out[i] = code;
  }
  return out;
}
